/* global window */
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import injectSheet from 'react-jss';

import { sliceColourTheme } from '../../styles/theme';
import { colourThemeType } from '../../propTypes';
import { useToast, ToastProvider } from '../Toast/ToastProvider';
import { sponsoredBrand } from './propTypes';
import { requestUpdateBrandFavourite } from '../../actions/favouritedBrands';
import SponsoredBrandBanner from './SponsoredBrandBanner';
import { getSponsoredBrandWithSliceId } from '../../selectors/sponsoredBrands';
import get from '../../helpers/get';
import { getSliceColourTheme, isGuestAccount } from '../../selectors';
import SponsoredProducts from './SponsoredProducts';
import useElementVisible from '../../hooks/useElementVisible';
import ErrorBoundary from '../ErrorBoundary';

const VISIBILITY_OPTIONS = {
  root: null,
  rootMargin: '0px',
  threshold: 0.5,
};

export const SponsoredBrandProductsComponent = ({
  sliceId,
  colourTheme,
  isGuest,
  brand,
  onFavouriteChange,
  isLoading,
  classes,
}) => {
  const { showError } = useToast();
  const impressionFired = useRef(false);

  const [containerRef, isVisible] = useElementVisible(VISIBILITY_OPTIONS);

  useEffect(() => {
    if (brand?.error) {
      showError(brand.error);
    }
  }, [brand?.error, showError]);

  useEffect(() => {
    if (isVisible && brand && !impressionFired.current) {
      impressionFired.current = true;
      if (typeof window !== 'undefined') {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'ti.sponsored.brand.impression',
          eventData: {
            nonInteraction: false,
            category: 'Landing Page - Sponsored Brand Impression',
            action: 'Impression',
            label: `Impression of ${brand.brandName}`,
            ad_id: brand.id,
          },
        });
      }
    }
  }, [isVisible, brand]);

  const handleCtaClick = () => {
    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'ga.event.prismic',
        eventData: {
          nonInteraction: false,
          category: 'Sponsored Brand Products',
          action: 'Click of Shop Brand',
          label: `Click of Shop ${brand.brandName}`,
          products: [
            {
              ad_id: brand.id,
              affiliation: 'citrus',
            },
          ],
        },
      });
    }
  };

  const handleFavouriteTrack = () => {
    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'ga.event.prismic',
        eventData: {
          nonInteraction: false,
          category: 'Sponsored Brand Products',
          action: 'Favourite the Brand',
          label: brand.status
            ? `Click of Remove Favourite ${brand.brandName}`
            : `Click of Favourite ${brand.brandName}`,
          products: [
            {
              sku: brand.brandId,
              position: sliceId,
              list: 'Landing Page - Sponsored Brand Products Carousel',
              quantity: 1,
              ad_id: brand.id,
              affiliation: 'citrus',
            },
          ],
        },
      });
    }
  };

  const handleFavouriteChange = () => {
    if (brand.busy) {
      return;
    }

    if (brand) {
      onFavouriteChange(brand.brandId, !brand.status);
    }
  };

  if (!brand && !isLoading) {
    return null;
  }

  return (
    <section ref={containerRef} className={classes?.section}>
      <SponsoredBrandBanner
        isGuest={isGuest}
        brand={brand}
        onCtaClick={handleCtaClick}
        onFavouriteTrack={handleFavouriteTrack}
        onFavouriteChange={handleFavouriteChange}
        isLoading={isLoading}
      />
      {brand?.products?.length > 0 ? (
        <SponsoredProducts
          sliceId={sliceId}
          colourTheme={colourTheme}
          adId={brand?.id}
        />
      ) : null}
    </section>
  );
};

SponsoredBrandProductsComponent.propTypes = {
  sliceId: PropTypes.number.isRequired,
  colourTheme: colourThemeType,
  isGuest: PropTypes.bool.isRequired,
  brand: sponsoredBrand,
  onFavouriteChange: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
};

SponsoredBrandProductsComponent.defaultProps = {
  colourTheme: sliceColourTheme.white,
  brand: null,
  isLoading: false,
};

const styles = {
  section: {
    paddingBottom: 16,
  },
};

const mapStateToProps = (state, { sliceId }) => ({
  colourTheme: sliceColourTheme[getSliceColourTheme(state, sliceId)],
  brand: getSponsoredBrandWithSliceId(state, sliceId),
  isGuest: isGuestAccount(state),
  isLoading: get(state, ['sponsoredBrands', 'busy']),
});

const mapDispatchToProps = dispatch => ({
  onFavouriteChange: (brandId, status) => dispatch(requestUpdateBrandFavourite(brandId, status)),
});

const withToastProvider = Component => props => (
  <ErrorBoundary errorMessage="ERROR: Something went wrong loading sponsored brand products">
    <ToastProvider>
      <Component {...props} />
    </ToastProvider>
  </ErrorBoundary>
);

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  injectSheet(styles),
  withToastProvider,
)(SponsoredBrandProductsComponent);
